// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Rediger mode's pure logic (frontend-u0o.1): its sub-tab links, the
 * sub-tab `/rediger` opens on, the Import sub-tab's source and its history
 * (the import jobs the event stream holds). Pure: no React, no DOM.
 */

import type { Job, JobStatus } from '../api/types';
import { REDIGER_PATH } from '../app/links';
import { importSummary } from './bimImport';
import { importJobSummary } from '../posegraf/rtabmapImport';

export type RedigerTab = 'scan' | 'geometri' | 'import';

/** `/rediger/<tab>[?query]`. */
export function redigerHref(tab: RedigerTab, query?: Readonly<Record<string, string>>): string {
  const q = new URLSearchParams(query ?? {}).toString();
  return q ? `${REDIGER_PATH}/${tab}?${q}` : `${REDIGER_PATH}/${tab}`;
}

/**
 * The query keys only Rediger's sub-tabs read: Scan & pose's selection,
 * pair and editor state (the old `/graph-view` deep links) and Import's
 * source. Leaving the mode drops them; every other key (storey, pano, …)
 * travels like in any other mode.
 */
export const REDIGER_LOCAL_KEYS: readonly string[] = ['edge', 'a', 'b', 'par', 'tilstand', 'source'];

/** `search` without Rediger's own keys (no leading `?`). */
export function stripRedigerKeys(search: string): string {
  const q = new URLSearchParams(search);
  for (const key of REDIGER_LOCAL_KEYS) q.delete(key);
  return q.toString();
}

/**
 * What decides the sub-tab `/rediger` opens on. `undefined` = still
 * loading; `null` = not known (an older server without the route), which
 * counts as none.
 */
export interface RedigerFacts {
  walls: number | null | undefined;
  spaces: number | null | undefined;
  sensorFrames: number | null | undefined;
}

/**
 * Geometri when the case has a building model, else Scan & pose when it has
 * sensor frames, else Import; null while a fact is still loading.
 */
export function defaultRedigerTab(f: RedigerFacts): RedigerTab | null {
  if (f.walls === undefined || f.spaces === undefined || f.sensorFrames === undefined) return null;
  if ((f.walls ?? 0) > 0 || (f.spaces ?? 0) > 0) return 'geometri';
  if ((f.sensorFrames ?? 0) > 0) return 'scan';
  return 'import';
}

// ------------------------------------------------------------- import --

export type ImportSource = 'rtabmap' | 'speckle' | 'ifc';

export interface ImportSourceDef {
  id: ImportSource;
  /** The source list's label. */
  label: string;
  /** Its second line: what it brings into the case. */
  hint: string;
  /** The history table's short name. */
  short: string;
  /** The job stage it runs as. */
  stage: string;
}

/** In the source list's order. */
export const IMPORT_SOURCES: readonly ImportSourceDef[] = [
  { id: 'rtabmap', label: 'RTAB-Map-sessioner', hint: 'Scanninger: billeder, dybde og poser', short: 'RTAB-Map', stage: 'import_rtabmap' },
  { id: 'speckle', label: 'Speckle (Revit)', hint: 'BIM-model: etager, vægge, åbninger, rum', short: 'Speckle', stage: 'import_speckle' },
  { id: 'ifc', label: 'IFC-fil', hint: 'BIM-model fra en IFC-fil', short: 'IFC', stage: 'import_ifc' },
];

/** `?source=`; anything else is the first source. */
export function parseImportSource(search: string): ImportSource {
  const v = new URLSearchParams(search).get('source');
  return IMPORT_SOURCES.find((s) => s.id === v)?.id ?? IMPORT_SOURCES[0].id;
}

const STATUS_LABEL: Record<JobStatus, string> = {
  queued: 'I kø',
  running: 'Kører',
  succeeded: 'Færdig',
  failed: 'Fejlede',
  cancelled: 'Annulleret',
};

/** The status pill's tone (`components/Pill`'s semantic tones). */
export type HistoryTone = 'good' | 'accent' | 'crit' | 'wait';

const STATUS_TONE: Record<JobStatus, HistoryTone> = {
  queued: 'wait',
  running: 'accent',
  succeeded: 'good',
  failed: 'crit',
  cancelled: 'wait',
};

export interface ImportHistoryRow {
  id: string;
  source: ImportSource;
  sourceLabel: string;
  /** The uploaded file's or session's name, when the job carries one. */
  name: string | null;
  /** `submitted_at` (ISO). */
  when: string;
  status: JobStatus;
  statusLabel: string;
  tone: HistoryTone;
  /** What it wrote (succeeded), why it failed, or null (still running, or cancelled without a reason). */
  written: string | null;
}

/**
 * The import jobs the event stream knows of, newest first: the Import
 * sub-tab's history. The server keeps no import ledger of its own yet
 * (no `GET /imports`), so this is the job list's view of it.
 */
export function importHistory(jobs: readonly Job[]): ImportHistoryRow[] {
  const rows: ImportHistoryRow[] = [];
  for (const job of jobs) {
    const src = IMPORT_SOURCES.find((s) => s.stage === job.stage);
    if (!src) continue;
    const name = typeof job.parameters?.name === 'string' && job.parameters.name.trim() !== '' ? job.parameters.name : null;
    let written: string | null = null;
    if (job.status === 'succeeded') written = src.id === 'rtabmap' ? importJobSummary(job) : importSummary(job.result);
    else if (job.status === 'failed' || job.status === 'cancelled') written = job.error?.trim() || null;
    rows.push({
      id: job.id,
      source: src.id,
      sourceLabel: src.short,
      name,
      when: job.submitted_at,
      status: job.status,
      statusLabel: STATUS_LABEL[job.status],
      tone: STATUS_TONE[job.status],
      written,
    });
  }
  return rows.sort((a, b) => (a.when < b.when ? 1 : a.when > b.when ? -1 : 0));
}
